import { useCallback, useEffect, useId, useRef } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { Dialog, DialogContent } from "@jhool-io/fe-components";
import { ISSOLaunchResponse } from "../../types/e-prescribe.types";
import {
    useSyncClientClinicalData,
    useSyncPracticeLevelPrescriptions,
    useSyncPrescriptionData,
} from "../../hooks/e-prescribe.mutations";
import useToast from "../../../../hooks/useToast";

interface DrFirstIframeProps {
    launchData: ISSOLaunchResponse | null;
    onClose: () => void;
    title: string;
    clientId?: string;
    practiceId: string;
    noteDetails?: {
        provider_id?: string;
        note_id?: string;
        session_history_id?: string;
    };
    showFooter?: boolean;
}

export default function DrFirstIframe({
    launchData,
    onClose,
    title,
    clientId,
    practiceId,
    noteDetails,
    showFooter,
}: DrFirstIframeProps) {
    const formRef = useRef<HTMLFormElement | null>(null);
    const iframeRef = useRef<HTMLIFrameElement | null>(null);
    const hasSubmittedRef = useRef(false);
    const iframeName = useId().replace(/:/g, "");

    const { toast } = useToast();

    const syncClientClinicalData = useSyncClientClinicalData();
    const syncPrescriptionData = useSyncPrescriptionData();
    const syncPracticePrescriptions =
        useSyncPracticeLevelPrescriptions(practiceId);

    const queryClient = useQueryClient();

    const submitLaunchForm = useCallback(() => {
        if (
            !launchData ||
            !formRef.current ||
            !iframeRef.current ||
            hasSubmittedRef.current
        ) {
            return;
        }

        hasSubmittedRef.current = true;

        requestAnimationFrame(() => {
            if (!formRef.current) {
                return;
            }

            formRef.current.submit();
        });
    }, [launchData]);

    const handleIframCloseWithoutClientId = () => {
        syncPracticePrescriptions.mutate();
        onClose();
    };

    const handleIFrameClose = async () => {
        const results = await Promise.allSettled([
            syncClientClinicalData.mutateAsync({
                practiceId,
                clientId: clientId ?? "",
            }),
            syncPrescriptionData.mutateAsync({
                practiceId,
                clientId: clientId ?? "",
                payload: {
                    provider_id: noteDetails?.provider_id,
                    note_id: noteDetails?.note_id,
                    session_history_id: noteDetails?.session_history_id,
                },
            }),
        ]);

        const hasSuccess = results.some(
            (result) => result.status === "fulfilled"
        );
        const rejectedResults = results.filter(
            (result): result is PromiseRejectedResult =>
                result.status === "rejected"
        );

        if (hasSuccess) {
            toast({
                mode: "success",
                message: "Client details updated successfully",
            });
        }

        rejectedResults.forEach((result) => {
            toast({
                mode: "error",
                message:
                    result.reason?.response?.data?.message ??
                    result.reason?.message ??
                    "Error updating client details",
            });
        });

        queryClient.invalidateQueries({
            queryKey: ["client-allergies", clientId],
        });
        queryClient.invalidateQueries({
            queryKey: ["client-medications", clientId],
        });
        queryClient.invalidateQueries({
            queryKey: ["prescriptions"],
        });

        onClose();
    };

    useEffect(() => {
        if (!launchData) {
            hasSubmittedRef.current = false;
            return;
        }

        submitLaunchForm();
    }, [launchData, submitLaunchForm]);

    if (!launchData) {
        return null;
    }

    return (
        <Dialog open>
            <DialogContent
                title={title}
                handleCloseDialog={
                    clientId
                        ? handleIFrameClose
                        : handleIframCloseWithoutClientId
                }
                wrapperClass="w-[950px]"
                onSaveClick={
                    clientId
                        ? handleIFrameClose
                        : handleIframCloseWithoutClientId
                }
                isSubmitBtnDisabled={syncClientClinicalData.isLoading}
                isCancelBtnDisabled={syncClientClinicalData.isLoading}
                showFooter={showFooter}
            >
                <div>
                    <form
                        ref={(node) => {
                            formRef.current = node;
                            submitLaunchForm();
                        }}
                        action={launchData.landingUrl}
                        method="post"
                        target={iframeName}
                        className="hidden"
                    >
                        <input
                            type="hidden"
                            name="token"
                            value={launchData.token}
                        />
                    </form>
                    <iframe
                        ref={(node) => {
                            iframeRef.current = node;
                            submitLaunchForm();
                        }}
                        name={iframeName}
                        id={iframeName}
                        width="100%"
                        height="800px"
                        title={title}
                        className="border-0"
                    />
                </div>
            </DialogContent>
        </Dialog>
    );
}
